import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Button } from "../../ui/Button";
import { cn } from "../../../lib/utils";
import { SystemTooltipContent } from "../SystemTooltip";

export interface ButtonCircleProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "color"
> {
  variant?: "default" | "critical" | "warning" | "success";
  /** Button size variant */
  size?: "large" | "medium" | "small";
  icon: React.ReactNode;
  /** Tooltip text shown on hover */
  tooltip?: string;
  /** Position of the tooltip relative to the button */
  tooltipSide?: "top" | "right" | "bottom" | "left";
  /** When true, button displays in active state */
  isActive?: boolean;
}

/**
 * ButtonCircle - Circular icon button with optional tooltip
 *
 * States:
 * - Default: Transparent background
 * - Hover: Hover background (#f1f5f9) with blue icon (#0000c9)
 * - Active: Active background (#e2e8f0) with blue icon
 * - Disabled: Disabled styling
 */
function ButtonCircle({
  className,
  variant = "default",
  size = "medium",
  icon,
  tooltip,
  tooltipSide = "top",
  isActive = false,
  disabled,
  ref,
  ...props
}: ButtonCircleProps & { ref?: React.Ref<HTMLButtonElement> }) {
  const buttonVariant =
    variant === "default"
      ? "circle"
      : variant === "critical"
        ? "circle-critical"
        : variant === "warning"
          ? "circle-warning"
          : "circle-success";

  const buttonSize =
    size === "large"
      ? "icon-large"
      : size === "medium"
        ? "icon-medium"
        : "icon-small";

  // Check if dropdown is open via aria-expanded or data-state
  const ariaExpanded = props["aria-expanded"];
  const dataState = (props as Record<string, unknown>)["data-state"];
  const isActiveState =
    isActive ||
    ariaExpanded === true ||
    ariaExpanded === "true" ||
    dataState === "open";

  const button = (
    <Button
      variant={buttonVariant}
      size={buttonSize}
      className={cn(
        "px-0",
        // Hover styles: light gray bg + blue icon
        "hover:bg-bg-hover hover:text-icon-blue",
        // Active (pressed) styles
        "active:bg-bg-active active:text-icon-blue",
        // Disabled styles
        "disabled:bg-bg-disabled disabled:text-text-disabled",
        className,
      )}
      // Active state styles via inline style (higher specificity, overrides hover)
      style={
        isActiveState
          ? { backgroundColor: "#e2e8f0", color: "#0000c9" }
          : undefined
      }
      ref={ref}
      disabled={disabled}
      aria-label={tooltip}
      data-state={isActiveState ? "active" : undefined}
      {...props}
    >
      {icon}
    </Button>
  );

  // If no tooltip, return button directly
  if (!tooltip) {
    return button;
  }

  // Wrap with tooltip when tooltip text is provided
  return (
    <TooltipPrimitive.Provider delayDuration={100}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{button}</TooltipPrimitive.Trigger>
        <SystemTooltipContent side={tooltipSide}>
          {tooltip}
        </SystemTooltipContent>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
ButtonCircle.displayName = "ButtonCircle";

export { ButtonCircle };
